import React, { useState, useEffect } from 'react';
import {
  BellIcon,
  XMarkIcon,
  ClockIcon,
  UserIcon,
  PhoneIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { BellIcon as BellSolidIcon } from '@heroicons/react/24/solid';

const NotificationCenter = ({ isOpen, onClose, notifications, onMarkAsRead, onMarkAllAsRead }) => {
  const [filter, setFilter] = useState('all'); // all, today, overdue, upcoming

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'follow_up_due':
        return <ClockIcon className="h-5 w-5 text-orange-500" />;
      case 'follow_up_overdue':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'lead_assigned':
        return <UserIcon className="h-5 w-5 text-blue-500" />;
      case 'follow_up_completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'reminder':
        return <BellSolidIcon className="h-5 w-5 text-purple-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'follow_up_due':
        return 'border-l-orange-500 bg-orange-50';
      case 'follow_up_overdue':
        return 'border-l-red-500 bg-red-50';
      case 'lead_assigned':
        return 'border-l-blue-500 bg-blue-50';
      case 'follow_up_completed':
        return 'border-l-green-500 bg-green-50';
      case 'reminder':
        return 'border-l-purple-500 bg-purple-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      urgent: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };
    return badges[priority] || badges.medium;
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInMinutes = Math.floor((now - notificationDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const filteredNotifications = notifications.filter(notification => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const notificationDate = new Date(notification.followUpDate || notification.createdAt);
    notificationDate.setHours(0, 0, 0, 0);
    
    switch (filter) {
      case 'today':
        return notificationDate.getTime() === today.getTime();
      case 'overdue':
        return notification.type === 'follow_up_overdue' || 
               (notificationDate < today && notification.type === 'follow_up_due');
      case 'upcoming':
        return notificationDate > today;
      default:
        return true;
    }
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-16 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center">
            <div className="bg-blue-100 p-2 rounded-lg mr-3">
              <BellSolidIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Notifications</h2>
              <p className="text-sm text-gray-600">{notifications.length} total notifications</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {notifications.some(n => !n.read) && (
              <button
                onClick={onMarkAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Mark all as read
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { key: 'all', label: 'All', count: notifications.length },
              { key: 'today', label: 'Today', count: notifications.filter(n => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const nDate = new Date(n.followUpDate || n.createdAt);
                nDate.setHours(0, 0, 0, 0);
                return nDate.getTime() === today.getTime();
              }).length },
              { key: 'overdue', label: 'Overdue', count: notifications.filter(n => 
                n.type === 'follow_up_overdue').length },
              { key: 'upcoming', label: 'Upcoming', count: notifications.filter(n => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const nDate = new Date(n.followUpDate || n.createdAt);
                nDate.setHours(0, 0, 0, 0);
                return nDate > today;
              }).length }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  filter === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label} {tab.count > 0 && (
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                    filter === tab.key ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto max-h-96">
          {filteredNotifications.length === 0 ? (
            <div className="p-8 text-center">
              <BellIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications</h3>
              <p className="text-gray-600">You're all caught up! No {filter !== 'all' ? filter : ''} notifications.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-gray-50 transition-colors border-l-4 ${getNotificationColor(notification.type)} ${
                    !notification.read ? 'bg-blue-25' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                            {notification.title}
                          </h4>
                          {notification.priority && (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityBadge(notification.priority)}`}>
                              {notification.priority.toUpperCase()}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2">
                          {notification.message}
                        </p>
                        
                        {notification.leadName && (
                          <div className="flex items-center text-xs text-gray-500 mb-2">
                            <UserIcon className="h-3 w-3 mr-1" />
                            <span className="font-medium">{notification.leadName}</span>
                            {notification.leadPhone && (
                              <>
                                <span className="mx-2">•</span>
                                <PhoneIcon className="h-3 w-3 mr-1" />
                                <span>{notification.leadPhone}</span>
                              </>
                            )}
                          </div>
                        )}
                        
                        {notification.followUpDate && (
                          <div className="flex items-center text-xs text-gray-500 mb-2">
                            <CalendarDaysIcon className="h-3 w-3 mr-1" />
                            <span>Follow-up: {new Date(notification.followUpDate).toLocaleDateString()}</span>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">
                            {formatTimeAgo(notification.createdAt)}
                          </span>
                          
                          {!notification.read && (
                            <button
                              onClick={() => onMarkAsRead(notification.id)}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Mark as read
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {!notification.read && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2"></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              {notifications.filter(n => !n.read).length} unread notifications
            </span>
            <button
              onClick={onClose}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;
